import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  fetchRbacAuditEvents,
  readSession,
  humanizeApiError,
  type RbacAuditEvent,
} from '../lib/api.js';

type AuditProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; events: RbacAuditEvent[]; total: number };

const PAGE_SIZE = 50;

const EVENT_TYPE_LABELS: Record<string, string> = {
  permission_denied: 'Permission denied',
  user_invited: 'User invited',
  user_revoked: 'User revoked',
  role_assigned: 'Role assigned',
  role_changed: 'Role changed',
  impersonation_started: 'Impersonation started',
  impersonation_ended: 'Impersonation ended',
};

const EVENT_TYPE_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All events' },
  { value: 'permission_denied', label: 'Permission denied' },
  { value: 'role_assigned', label: 'Role assigned' },
  { value: 'role_changed', label: 'Role changed' },
  { value: 'user_invited', label: 'User invited' },
  { value: 'user_revoked', label: 'User revoked' },
  { value: 'impersonation_started', label: 'Impersonation started' },
];

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function formatPayload(payload: Record<string, unknown> | null): string {
  if (!payload) return '—';
  try {
    return JSON.stringify(payload);
  } catch {
    return '[unserializable]';
  }
}

export function Audit({ config }: AuditProps): JSX.Element {
  const [eventType, setEventType] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const apiUrl = config.deployment.apiUrl;

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const res = await fetchRbacAuditEvents(apiUrl, token, {
        limit: PAGE_SIZE,
        offset,
        eventType: eventType || undefined,
      });
      setState({
        kind: 'ready',
        events: res.events,
        total: res.total,
      });
    } catch (err) {
      const message = humanizeApiError(err instanceof Error ? err.message : null);
      setState({ kind: 'error', message });
    }
  }, [apiUrl, eventType, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const pageInfo = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const start = state.events.length === 0 ? 0 : offset + 1;
    const end = offset + state.events.length;
    return `${start}–${end} of ${state.total}`;
  }, [state, offset]);

  const loading = state.kind === 'loading';
  const hasPrev = offset > 0;
  const hasNext = state.kind === 'ready' && offset + state.events.length < state.total;

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <h1 className="ferro-section__title">Audit log</h1>
      <p className="ferro-muted">
        RBAC events for this tenant — denied requests, role changes, and impersonations.
      </p>

      <div className="ferro-toolbar">
        <label className="ferro-field">
          <span className="ferro-field__label">Event type</span>
          <select
            className="ferro-input"
            value={eventType}
            onChange={(e) => {
              setOffset(0);
              setEventType(e.target.value);
            }}
          >
            {EVENT_TYPE_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading…
        </p>
      ) : state.kind === 'error' ? (
        <p className="ferro-error" role="alert">
          {state.message}
        </p>
      ) : state.events.length === 0 ? (
        <p className="ferro-muted">No events match the current filter.</p>
      ) : (
        <div className="ferro-table-card">
          <div className="ferro-table-scroll">
            <table className="ferro-admin-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Event</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Target</th>
                  <th scope="col">Detail</th>
                </tr>
              </thead>
              <tbody>
                {state.events.map((evt) => (
                  <tr key={evt.id}>
                    <td>{formatTimestamp(evt.createdAt)}</td>
                    <td>{EVENT_TYPE_LABELS[evt.eventType] ?? evt.eventType}</td>
                    <td>
                      {evt.actorUserId ? (
                        <span title={evt.actorKind}>{evt.actorUserId}</span>
                      ) : (
                        <span className="ferro-muted">{evt.actorKind}</span>
                      )}
                    </td>
                    <td>{evt.targetUserId ?? '—'}</td>
                    <td className="ferro-mono ferro-clip">{formatPayload(evt.payload)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="ferro-toolbar">
        {pageInfo ? <span className="ferro-muted">{pageInfo}</span> : null}
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          disabled={!hasPrev || loading}
          onClick={() => setOffset((o) => Math.max(o - PAGE_SIZE, 0))}
        >
          Previous
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          disabled={!hasNext || loading}
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
        >
          Next
        </button>
      </div>
    </section>
  );
}
